import React from "react";
import gql from "graphql-tag";
import { useMutation } from "react-apollo";
import { ApolloError } from "apollo-client";
import { Bugsnag } from "@services";
import { Box, Flex, Text, TouchableOpacity } from "@atoms";
import { GroupSubHeader } from "../GroupSubHeader";
import { RemoveEmptyGroup, RemoveEmptyGroupVariables } from "./__generated__/RemoveEmptyGroup";
// import styled from "@emotion/styled";

// == Types ================================================================

interface IProps {
  id: string;
  name: string | null;
}

// == Constants ============================================================

NoMemberSubGroupHeader.defaultProps = {};

const REMOVE_EMPTY_GROUP_MUTATION = gql`
  mutation RemoveEmptyGroup($id: ID!) {
    CampaignGroupDelete(id: $id) {
      ... on MutationError {
        errors {
          input
          inputErrors
        }
      }
      ... on Campaign {
        id
        name
        groups {
          id
          name
        }
      }
    }
  }
`;

// == Component ============================================================

export default function NoMemberSubGroupHeader({ id, name }: IProps) {
  const onError = (error?: ApolloError) => {
    if (error) Bugsnag.notify(error);
    Materialize?.toast?.(
      `There was an ERROR removing ${name} from this campaign. Please try again.`,
      4000,
      "rounded toast-danger"
    );
  };

  const [createRemoveEmptyGroup, { loading }] = useMutation<
    RemoveEmptyGroup,
    RemoveEmptyGroupVariables
  >(REMOVE_EMPTY_GROUP_MUTATION, {
    onError,
    onCompleted: ({ CampaignGroupDelete }) => {
      if (CampaignGroupDelete.__typename === "MutationError") {
        onError();
        return;
      }
      Materialize?.toast?.(
        `${name} has been successfully removed from this campaign`,
        4000,
        "rounded toast-success"
      );
    },
  });

  const deleteEmptyGroup = () => {
    createRemoveEmptyGroup({ variables: { id } });
  };

  if (id === "__pending__" || id === "__noGroup__") return null;

  return (
    <Box>
      <GroupSubHeader name={name} />
      <Flex alignItems="center" justifyContent="flex-start" padding={[1, 2, 3]}>
        <Box paddingLeft={3}>
          <Text display="block" fontSize={[1, 2]}>
            No Members in this Group.
          </Text>
          {name !== "__noGroup__" && (
            <TouchableOpacity onTouch={deleteEmptyGroup}>
              <Text color="primary">Delete this Group.</Text>
            </TouchableOpacity>
          )}
        </Box>
      </Flex>
    </Box>
  );
}

// == Styles ===============================================================
